import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import CategorySelector from "./CategorySelector";
import BannerSearch from "./SearchField";
import { get_top_banner } from "../../api/ControllApi";
import { useCachedApi } from "../../hooks/useCachedApi";

// ── Static slider config — hoisted outside component to prevent recreation ──
const sliderSettings = {
  dots: true,
  infinite: true,
  autoplay: true,
  speed: 600,
  autoplaySpeed: 3000,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
};

const Herobanner = () => {
  const navigate = useNavigate();
  const { requestApi } = useCachedApi();
  const [topBanner, setTopBanner] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [category, setCategory] = useState("all");

  const handleImageLoad = useCallback((src) => {
    setLoadedImages((prev) => ({ ...prev, [src]: true }));
  }, []);

  useEffect(() => {
    setLoading(true);
    requestApi("top_banner", get_top_banner)
      .then((res) => setTopBanner(res?.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [requestApi]);

  // beforeChange stays inline since it depends on setCurrentSlide — that ref is stable
  const handleBeforeChange = useCallback((_, next) => setCurrentSlide(next), []);

  const handleBannerClick = useCallback((url) => {
    if (!url) return;
    try {
      const urlObj = new URL(url);
      if (urlObj.origin === window.location.origin) {
        navigate(urlObj.pathname + urlObj.search);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      navigate(url);
    }
  }, [navigate]);

  const activeBanner = topBanner[currentSlide] || {};
  const BannerSkeleton = () => (
    <Box sx={{ px: 0 }}>
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: { xs: 500, md: 650 },
          bgcolor: "rgba(0,0,0,0.1)",
        }}
      />
    </Box>
  );

  // Merge beforeChange into sliderSettings per-instance so the static object stays pure
  const settings = { ...sliderSettings, beforeChange: handleBeforeChange };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>

      {loading ? <BannerSkeleton /> :
        <>
          <Slider {...settings}>
            {topBanner.map((slide, index) => (

              <Box
                key={index}
                onClick={() => handleBannerClick(slide?.redirect_url)}
                sx={{
                  minHeight: { xs: "500px", md: "650px" },
                  position: "relative",
                  cursor: slide?.redirect_url ? "pointer" : "default",
                  overflow: "hidden",
                }}
              >
                {!loadedImages[slide?.image] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                  />
                )}
                <Box
                  component="img"
                  src={slide?.image || "/assets/images/landing_bg.png"}
                  alt={slide?.header_text || "Geo Rental Farms banner"}
                  onLoad={() => handleImageLoad(slide?.image)}
                  // fetchPriority="high" on the first slide — it is the LCP element
                  fetchPriority={index === 0 ? "high" : "auto"}
                  width={1920}
                  height={650}
                  sx={{
                    width: "100%",
                    height: { xs: "500px", md: "650px" },
                    objectFit: "cover",
                    display: loadedImages[slide?.image] ? "block" : "none",
                    transition: "opacity 0.5s ease-in-out",
                    opacity: loadedImages[slide?.image] ? 1 : 0,
                  }}
                />
                {/* Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                  }}
                />
              </Box>
            ))}
          </Slider>



          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              color: "#fff",
              pointerEvents: "none",
            }}
          >

            {/* Dynamic Heading */}
            <Typography
              sx={{
                mt: { xs: 8, md: 12 },
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "3rem" },
                color: activeBanner?.header_color || "#fff",
              }}
            >
              {activeBanner?.header_text}
            </Typography>

            {/* Dynamic Subheading */}
            {activeBanner?.description_text && <Box
              sx={{
                border: {
                  xs: "1px solid #fff",
                  sm: "1.5px solid #fff",
                  md: "2px solid #fff",
                },

                px: { xs: 1.5, sm: 2.5, md: 3 },
                py: { xs: 0.5, sm: 0.7, md: 1 },

                mt: { xs: 1.5, sm: 2, md: 3 },

                maxWidth: {
                  xs: "95%",
                  sm: "85%",
                  md: "70%",
                  lg: "60%",
                },

                mx: "auto",
                textAlign: "center",
                borderRadius: { xs: 1, sm: 2 },

                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem", md: "1.1rem" },
                  color: activeBanner?.description_color || "#fff",
                  lineHeight: 1.5,
                }}
              >
                {activeBanner?.description_text}
              </Typography>
            </Box>}



            {/*STATIC SECTION */}
            {topBanner.length > 0 &&
              <>
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    mt: { xs: 2, sm: 4 },
                    width: { xs: "95%", sm: "85%", md: "75%", lg: "60%" },
                    pointerEvents: "auto",
                  }}
                >
                  <CategorySelector value={category} onChange={setCategory} />
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    width: { xs: "90%", md: "90%", lg: "60%" },
                    pointerEvents: "auto",
                  }}
                >
                  <BannerSearch type={category} />
                </Box>
              </>
            }

          </Box></>}

    </Box>
  );
};

export default Herobanner;
